/**
 * Product Gallery
 *
 * Drives the main product media gallery:
 *  - Mobile: a horizontal scroll-snap slider. Native swipe plus prev/next arrows.
 *  - Desktop: a static vertical stack of full images (no JS needed for layout).
 *  - Clicking any image (or the zoom button) opens the shared lightbox modal at
 *    that image's index via the same `modal-open` event the modal + lightbox use.
 */
class RcProductGallery extends HTMLElement {
  connectedCallback() {
    this.modalId = this.getAttribute('data-modal-id');
    this.track = this.querySelector('[data-pg-track]');
    this.slides = this.track ? [...this.track.querySelectorAll('[data-pg-slide]')] : [];
    this.prevBtn = this.querySelector('[data-pg-nav="prev"]');
    this.nextBtn = this.querySelector('[data-pg-nav="next"]');
    this.index = 0;

    this._onScroll = this._onScroll.bind(this);
    this._onOpen = this._onOpen.bind(this);
    this._onPrev = () => this._goTo(this.index - 1);
    this._onNext = () => this._goTo(this.index + 1);

    if (this.track) this.track.addEventListener('scroll', this._onScroll, { passive: true });
    this.prevBtn?.addEventListener('click', this._onPrev);
    this.nextBtn?.addEventListener('click', this._onNext);
    if (this.modalId) this.addEventListener('click', this._onOpen);

    this._updateNav();
  }

  disconnectedCallback() {
    if (this.track) this.track.removeEventListener('scroll', this._onScroll);
    this.prevBtn?.removeEventListener('click', this._onPrev);
    this.nextBtn?.removeEventListener('click', this._onNext);
    this.removeEventListener('click', this._onOpen);
  }

  _goTo(target) {
    if (!this.track || !this.slides.length) return;
    const i = Math.max(0, Math.min(target, this.slides.length - 1));
    const slide = this.slides[i];
    if (slide) this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  }

  _onScroll() {
    if (!this.track || !this.slides.length) return;
    const width = this.track.clientWidth || 1;
    const i = Math.round(this.track.scrollLeft / width);
    this.index = Math.max(0, Math.min(i, this.slides.length - 1));
    this._updateNav();
  }

  _updateNav() {
    if (this.prevBtn) this.prevBtn.disabled = this.index <= 0;
    if (this.nextBtn) this.nextBtn.disabled = this.index >= this.slides.length - 1;
  }

  _onOpen(e) {
    // Let native media/link controls work without opening the lightbox
    if (e.target.closest('video, iframe, a')) return;
    const trigger = e.target.closest('[data-pg-open], [data-slide-index]');
    if (!trigger || !this.contains(trigger)) return;

    let raw = trigger.getAttribute('data-slide-index');
    if (raw == null) {
      const slide = trigger.closest('[data-pg-slide]');
      raw = slide ? slide.getAttribute('data-slide-index') : null;
    }
    let idx = raw != null ? parseInt(raw, 10) : this.index;
    if (Number.isNaN(idx)) idx = this.index;

    window.dispatchEvent(
      new CustomEvent('modal-open', { detail: { id: this.modalId, activeIndex: idx } })
    );
  }
}

if (!customElements.get('rc-product-gallery')) {
  customElements.define('rc-product-gallery', RcProductGallery);
}
